import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { withStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import AuthUserContainer from '../../containers/AuthUser';
import { searchStations, preview, execute } from '../../modules/system/tankScrub';

const styles = (theme) => ({
	innerPaper: {
		padding: 10,
		textAlign: 'left',
		fontSize: 14,
		[theme.breakpoints.up('md')]: {
			padding: 40,
			paddingTop: 30,
		},
	},
	description: {
		color: '#666',
		marginBottom: 20,
	},
	warnings: {
		background: '#FFF8E1',
		border: '1px solid #FFE082',
		borderRadius: 4,
		padding: '8px 12px',
		margin: '16px 0px',
		fontSize: 13,
	},
	success: {
		background: '#E8F5E9',
		border: '1px solid #A5D6A7',
		borderRadius: 4,
		padding: '8px 12px',
		margin: '16px 0px',
		fontSize: 14,
	},
	sectionHeader: {
		textTransform: 'uppercase',
		fontSize: 13,
		color: '#777',
		fontWeight: 900,
		letterSpacing: 1,
		marginTop: 24,
		marginBottom: 8,
	},
	executeButton: {
		backgroundColor: '#d32f2f',
		color: '#fff',
		'&:hover': {
			backgroundColor: '#b71c1c',
		},
	},
});

class TankScrub extends Component {
	constructor(props) {
		super(props);
		this.state = {
			stations: undefined,
			selectedStation: null,
			oldTankNumber: '',
			newTankNumber: '',

			isWorking: false,
			previewResult: undefined,
			executeResult: undefined,
			warningsAcknowledged: false,
		};
	}

	componentDidMount() {
		searchStations({
			callback: (err, r) => {
				if (err) {
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({
					stations: _.get(r, ['collections', 'tankScrubStationCollection'], []),
				});
			},
		});
	}

	getScrubParams = () => {
		const { selectedStation, oldTankNumber, newTankNumber } = this.state;
		return {
			stationId: _.get(selectedStation, 'stationId'),
			oldTankNumber: parseInt(oldTankNumber, 10),
			newTankNumber: parseInt(newTankNumber, 10),
		};
	};

	canPreview = () => {
		const { stationId, oldTankNumber, newTankNumber } = this.getScrubParams();
		if (!stationId || _.isNaN(oldTankNumber) || _.isNaN(newTankNumber)) {
			return false;
		}
		return oldTankNumber !== newTankNumber;
	};

	handlePreview = () => {
		this.setState({ isWorking: true, previewResult: undefined, executeResult: undefined });
		preview({
			...this.getScrubParams(),
			callback: (err, r) => {
				if (err) {
					this.setState({ isWorking: false });
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({
					isWorking: false,
					previewResult: r,
					warningsAcknowledged: false,
				});
			},
		});
	};

	handleExecute = () => {
		const { previewResult, warningsAcknowledged } = this.state;
		const warnings = _.get(previewResult, 'warnings', []);
		if (!_.isEmpty(warnings) && !warningsAcknowledged) {
			return;
		}

		this.setState({ isWorking: true });
		execute({
			...this.getScrubParams(),
			acknowledgeWarnings: warningsAcknowledged,
			callback: (err, r) => {
				if (err) {
					this.setState({ isWorking: false });
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({
					isWorking: false,
					executeResult: r,
					previewResult: undefined,
					warningsAcknowledged: false,
				});
			},
		});
	};

	renderForm = () => {
		const { stations, selectedStation, oldTankNumber, newTankNumber, isWorking } = this.state;

		if (!stations) {
			return <div>Loading stations...</div>;
		}

		const tanks = _.get(selectedStation, 'tanks', []);

		return (
			<Grid container spacing={3} alignItems="flex-end">
				<Grid item xs={12} md={5}>
					<Autocomplete
						options={stations}
						getOptionLabel={(s) => `${s.number || '(no #)'} — ${s.name}`}
						value={selectedStation}
						onChange={(event, value) => {
							this.setState({
								selectedStation: value,
								newTankNumber: '',
								previewResult: undefined,
								executeResult: undefined,
							});
						}}
						renderInput={(params) => <TextField {...params} label="Station" />}
					/>
				</Grid>
				<Grid item xs={6} md={2}>
					<TextField
						fullWidth
						label="Old Tank #"
						type="number"
						value={oldTankNumber}
						onChange={(event) => {
							this.setState({ oldTankNumber: event.target.value, previewResult: undefined });
						}}
					/>
				</Grid>
				<Grid item xs={6} md={3}>
					<FormControl fullWidth disabled={!selectedStation}>
						<InputLabel>New Tank (from station config)</InputLabel>
						<Select
							value={newTankNumber}
							onChange={(event) => {
								this.setState({ newTankNumber: event.target.value, previewResult: undefined });
							}}
						>
							{tanks.map((tank) => (
								<MenuItem key={tank.tankNumber} value={tank.tankNumber}>
									{`Tank ${tank.tankNumber} — ${tank.productType || '?'}` +
										(tank.totalTankVolume
											? ` (${Number(tank.totalTankVolume).toLocaleString()} gal)`
											: '')}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={12} md={2}>
					<Button
						fullWidth
						color="primary"
						variant="contained"
						disabled={!this.canPreview() || isWorking}
						onClick={this.handlePreview}
					>
						Preview
					</Button>
				</Grid>
			</Grid>
		);
	};

	renderDeliveriesTable = (label, deliveries) => {
		const { classes } = this.props;
		return (
			<div key={label}>
				<div className={classes.sectionHeader}>
					{label} ({deliveries.length})
				</div>
				{_.isEmpty(deliveries) && <div>None</div>}
				{!_.isEmpty(deliveries) && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Delivery Id</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Delivery Window Start</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{deliveries.map((d) => (
								<TableRow key={d.stationDeliveryId}>
									<TableCell>{d.stationDeliveryId}</TableCell>
									<TableCell>{d.status}</TableCell>
									<TableCell>{d.deliveryWindowStart || '-'}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>
		);
	};

	renderPreview = () => {
		const { classes } = this.props;
		const { previewResult, warningsAcknowledged, isWorking } = this.state;
		if (!previewResult) {
			return null;
		}

		const { station, survey, warnings } = previewResult;
		const { oldTankNumber, newTankNumber } = this.getScrubParams();
		const hasWarnings = !_.isEmpty(warnings);

		return (
			<div>
				<Typography variant="h6" style={{ marginTop: 30 }}>
					Preview: {station.number} — {station.name}, Tank {oldTankNumber} → Tank {newTankNumber}
				</Typography>

				{hasWarnings && (
					<div className={classes.warnings}>
						{warnings.map((warning) => (
							<div key={warning}>⚠️ {warning}</div>
						))}
					</div>
				)}

				{this.renderDeliveriesTable(
					'Deliveries to repoint (primary leg)',
					survey.deliveriesPrimary
				)}
				{this.renderDeliveriesTable(
					'Deliveries to repoint (secondary leg)',
					survey.deliveriesSecondary
				)}

				<div className={classes.sectionHeader}>Inventory readings</div>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Tank</TableCell>
							<TableCell>Readings</TableCell>
							<TableCell>Product Types</TableCell>
							<TableCell>Earliest</TableCell>
							<TableCell>Latest</TableCell>
							<TableCell>Action</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>{oldTankNumber} (old)</TableCell>
							<TableCell>{survey.oldTankReadings.recordCount}</TableCell>
							<TableCell>{survey.oldTankReadings.productTypes || '-'}</TableCell>
							<TableCell>{survey.oldTankReadings.earliest || '-'}</TableCell>
							<TableCell>{survey.oldTankReadings.latest || '-'}</TableCell>
							<TableCell>Will be removed (hidden)</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>{newTankNumber} (new)</TableCell>
							<TableCell>{survey.newTankReadings.recordCount}</TableCell>
							<TableCell>{survey.newTankReadings.productTypes || '-'}</TableCell>
							<TableCell>{survey.newTankReadings.earliest || '-'}</TableCell>
							<TableCell>{survey.newTankReadings.latest || '-'}</TableCell>
							<TableCell>Kept as-is</TableCell>
						</TableRow>
					</TableBody>
				</Table>

				<div className={classes.sectionHeader}>Derived / related data</div>
				<div>
					tankDailySales rows for tank {oldTankNumber}: {survey.tankDailySalesCount} (will be
					deleted)
				</div>
				<div>
					ATG-detected delivery records for tank {oldTankNumber}: {survey.atgDeliveryRecordCount}{' '}
					(never changed)
				</div>

				<div style={{ marginTop: 24 }}>
					{hasWarnings && (
						<FormControlLabel
							control={
								<Checkbox
									checked={warningsAcknowledged}
									onChange={(event) => {
										this.setState({ warningsAcknowledged: event.target.checked });
									}}
								/>
							}
							label="I have read the warnings above and want to proceed anyway"
						/>
					)}
					<div style={{ marginTop: 8 }}>
						<Button
							variant="contained"
							className={classes.executeButton}
							disabled={isWorking || (hasWarnings && !warningsAcknowledged)}
							onClick={this.handleExecute}
						>
							Execute Scrub
						</Button>
					</div>
				</div>
			</div>
		);
	};

	renderExecuteResult = () => {
		const { classes } = this.props;
		const { executeResult } = this.state;
		if (!executeResult) {
			return null;
		}

		const { station, results } = executeResult;
		return (
			<div className={classes.success}>
				<strong>
					Scrub complete for {station.number} — {station.name}
				</strong>
				<div>Primary-leg deliveries repointed: {results.deliveriesPrimaryUpdated}</div>
				<div>Secondary-leg deliveries repointed: {results.deliveriesSecondaryUpdated}</div>
				<div>Inventory readings removed (hidden): {results.inventoryReadingsHidden}</div>
				<div>tankDailySales rows deleted: {results.tankDailySalesDeleted}</div>
			</div>
		);
	};

	render() {
		const { classes } = this.props;

		return (
			<Paper>
				<div className={classes.innerPaper}>
					<Typography variant="h5" gutterBottom>
						Tank Scrub
					</Typography>
					<div className={classes.description}>
						Fix a station whose tank was signalled under the wrong tank number. Moves every
						scheduled delivery from the old tank number to the correct one, removes the inventory
						readings collected under the old number (the correct tank&apos;s readings are kept), and
						purges the old tank&apos;s derived reporting rows. Preview first — nothing is written
						until you execute.
					</div>

					{this.renderForm()}
					{this.renderExecuteResult()}
					{this.renderPreview()}
				</div>
			</Paper>
		);
	}
}

TankScrub.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), AuthUserContainer])(TankScrub);
